import { ActionButton, PrimaryButton, Stack, Text, merge } from '@fluentui/react';
import { useBoolean, useConst } from '@fluentui/react-hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import padlockIcon from '../../../assets/icons/svg/padlock.svg';
import userIcon from '../../../assets/icons/svg/user.svg';
import { useAuthManager } from '../../../contexts/authContext';
import { useNotificationsManager } from '../../../contexts/notificationContext';
import { ResponsiveSize, useResponsiveSize } from '../../../contexts/responsiveContext';
import { Data } from '../../../data/Data';
import CustomModal from '../../CustomModal';
import FormField from '../../FormField';

const Form = () => {

    const formType = useConst({
        AUTH: 1,
        RESET: 2,
        CHANGE: 3
    });

    const { connect } = useAuthManager();
    const { dismiss, inform } = useNotificationsManager();
    const { responsiveSize } = useResponsiveSize();
    const location = useLocation();
    const navigate = useNavigate();

    const [displayedForm, setDisplayedForm] = useState(formType.AUTH);
    const [email, setEmail] = useState(undefined);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [successMessage, setSuccessMessage] = useState(undefined);
    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
    const [isRandomized, setIsRandomized] = useState(false);
    const [login, setLogin] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
    const [password, setPassword] = useState("");

    // Basculer automatiquement sur le formulaire de connexion à la fermeture de la modal
    useEffect(() => {
        if (!isModalOpen) setDisplayedForm(formType.AUTH);
    }, [formType, isModalOpen]);

    // Supprimer le message d'erreur à chaque bascule entre deux formulaires
    useEffect(() => {
        setErrorMessage(undefined);
    }, [displayedForm]);

    // Afficher le formulaire de réinitialisation du mot de passe si URL spécifique
    useEffect(() => {
        if (location.pathname.startsWith("/changePassword"))
            setDisplayedForm(formType.CHANGE);
        else setDisplayedForm(formType.AUTH);
    }, [formType, location.pathname]);

    useEffect(() => {
        if (!!newPassword && !!newPasswordConfirmation && newPassword !== newPasswordConfirmation)
            setErrorMessage("Les nouveaux mots de passe ne sont pas identiques");
        else setErrorMessage(undefined);
    }, [newPassword, newPasswordConfirmation]);

    const styles = useMemo(
        () => {
            let styles = {
                actionButton: {
                    flexContainer: { alignItems: "end" },
                    root: {
                        border: 0,
                        color: "#777777",
                        fontSize: 12,
                        height: "unset"
                    }
                },
                actionButtonIcon: {
                    root: {
                        height: 14,
                        fontSize: 12
                    }
                },
                button: {
                    root: {
                        marginBottom: 20,
                        width: 269
                    }
                },
                errorAndLink: {
                    root: {
                        padding: "4px 0 10px",
                        width: 269
                    }
                },
                error: {
                    root: {
                        color: "red",
                        fontSize: 12,
                        lineHeight: "14px",
                    }
                },
                fieldBottom: { root: { opacity: displayedForm === formType.RESET ? 0 : 1 } },
                fieldTop: { root: { paddingBottom: 24 } },
                hint: {
                    root: {
                        fontSize: 11,
                        lineHeight: "13px",
                        marginBottom: 16,
                        textAlign: "left"
                    }
                },
                link: {
                    root: {
                        color: "#777777",
                        cursor: "pointer",
                        fontSize: 12,
                        lineHeight: "14px",
                        textAlign: "right",
                        selectors: { "&:hover": { color: "red" } }
                    }
                },
                outer: {
                    root: {
                        background: "#ECECEC",
                        minHeight: 414,
                        padding: "22px 45px 20px 45px",
                        width: "100%"
                    }
                },
                success: {
                    root: {
                        color: "green",
                        fontSize: 12,
                        lineHeight: "14px",
                    }
                },
                title: {
                    root: {
                        color: "black",
                        fontFamily: "Kepler Std",
                        fontWeight: 700,
                        fontSize: 28,
                        lineHeight: "34px",
                        paddingBottom: 22,
                    }
                }
            };

            if (responsiveSize >= ResponsiveSize.medium)
                merge(
                    styles,
                    {
                        button: { root: { width: "auto" } },
                        errorAndLink: { root: { width: "auto" } },
                        hint: { root: { textAlign: "center" } },
                        outer: {
                            root: {
                                height: 498,
                                padding: "52px 55px 20px 55px",
                                width: 480
                            }
                        },
                        title: {
                            root: {
                                fontSize: 36,
                                lineHeight: "43px",
                                padding: "0 48px 52px",
                                textAlign: "center"
                            }
                        },
                    }
                );

            return styles;
        },
        [displayedForm, formType, responsiveSize]
    );

    const isFormValid = useMemo(() => {
        switch (displayedForm) {
            case formType.AUTH: return true; //return !!login && !!password; // Pas de test ; sur Chrome le bouton reste grisé même avec des valeurs pré-enregistrées (vu avec SL)
            case formType.RESET: return !!login;
            case formType.CHANGE:
                if (!newPassword || !newPasswordConfirmation) return false;
                if (newPassword !== newPasswordConfirmation) return false;
                //2811
                //const regex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[()#?!@$%^&*-]).{8,}$/;//20241015 : YD : 2790
                //const regex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;
                //const strongEnough = regex.test(newPassword);
                //if (!strongEnough) return false;

                return true;
            default: return false;
        }
    }, [displayedForm, formType, login, newPassword, newPasswordConfirmation]);

    const title = useMemo(() => {
        switch (displayedForm) {
            case formType.AUTH: return <Text styles={styles.title}>Connectez-vous<br />à votre espace<br />personnel :</Text>;
            case formType.RESET: return <Text styles={styles.title}>Réinitialisation<br />de votre<br />mot de passe :</Text>;
            case formType.CHANGE: return <Text styles={styles.title}>Votre nouveau<br />mot de passe :</Text>;
            default: return;
        }
    }, [displayedForm, formType, styles]);

    const buttonLabel = useMemo(() => {
        switch (displayedForm) {
            case formType.AUTH: return "Se connecter";
            case formType.RESET: return "Réinitialiser mot de passe";
            case formType.CHANGE: return "Modifier mot de passe";
            default: return;
        }
    }, [displayedForm, formType]);

    // formType.RESET
    const resetPassword = useCallback(async () => {
        const id = inform("Réinitialisation du mot de passe en cours...");
        const response = await Data._resetPassword(login);
        dismiss(id);
        if (response.codeRetour === 0) {
            const { email } = response.reponseAPIobjet;
            setEmail(email);
            showModal();
        }
        else return response;
    }, [dismiss, inform, login, showModal]);

    // formType.CHANGE
    const changePassword = useCallback(async () => {
        const urlParams = new URLSearchParams(location.search);
        const key = urlParams.get("key");
        const id = inform("Modification du mot de passe en cours...");
        const response = await Data._updatePasswordOffline(key, newPassword, newPasswordConfirmation);
        dismiss(id);
        if (response.codeRetour === 0) {
            navigate("/");
            setSuccessMessage("Modification effectuée avec succès");
        }
        else return response;
    }, [dismiss, inform, location, navigate, newPassword, newPasswordConfirmation]);

    const onClick = useCallback(async () => {

        setErrorMessage(undefined);

        let response = undefined;
        if (displayedForm === formType.AUTH) response = await connect(login, password);
        if (displayedForm === formType.RESET) response = await resetPassword();
        if (displayedForm === formType.CHANGE) response = await changePassword();

        if (!!response) {
            const { messageInfo, messageRetour } = response;
            if (!!messageInfo) setErrorMessage(messageInfo);
            else if (!!messageRetour) setErrorMessage(messageRetour);
        }

    }, [changePassword, connect, displayedForm, formType, login, password, resetPassword]);

    // Oubli du mot de passe
    const toggleForm = useCallback(() => {
        if (displayedForm === formType.AUTH) setDisplayedForm(formType.RESET);
        else if (displayedForm !== formType.AUTH) setDisplayedForm(formType.AUTH);

        if (location.pathname !== ("/")) navigate("/");
    }, [displayedForm, formType, location.pathname, navigate]);

    // Génération d'un mot de passe aléatoire
    const _randomize = useCallback(() => {
        setErrorMessage(undefined);
        Data._generatePassword().then(response => {
            if (response.codeRetour === 0) {
                setNewPassword(response.reponseAPIobjet);
                setNewPasswordConfirmation(response.reponseAPIobjet);
                setIsRandomized(true);
            }
            else {
                const { messageInfo, messageRetour } = response;
                if (!!messageInfo) setErrorMessage(messageInfo);
                else if (!!messageRetour) setErrorMessage(messageRetour);
            }
        });
    }, []);

    const _onCancel = useCallback(() => {
        setNewPassword("");
        setNewPasswordConfirmation("");
        setIsRandomized(false);
    }, []);

    // UI
    const _messages = useMemo(() => {
        return !!errorMessage
            ? <Text id="txtError" styles={styles.error}>{errorMessage}</Text>
            : !!successMessage && <Text id="txtSuccess" styles={styles.success}>{successMessage}</Text>
    }, [errorMessage, styles.error, styles.success, successMessage]);

    // RENDER
    return <>

        <Stack styles={styles.outer}>

            {title}

            {displayedForm === formType.CHANGE &&
                <Text styles={styles.hint}>Le nouveau mot de passe doit contenir au moins 12 caractères, dont au moins 1 majuscule, 1 minuscule, 1 chiffre et 1 caractère spécial.</Text>
            }

            {displayedForm !== formType.CHANGE && <>
                <FormField
                    customStyles={styles.fieldTop}
                    id="tfLogin"
                    onChange={(event, newValue) => setLogin(newValue)}
                    placeholder="Votre login"
                    prefixIcon={userIcon}
                    value={login}
                />
                <FormField
                    customStyles={styles.fieldBottom}
                    disabled={displayedForm !== formType.AUTH}
                    id="tfPassword"
                    onChange={(event, newValue) => setPassword(newValue)}
                    placeholder={"Votre mot de passe"}
                    prefixIcon={padlockIcon}
                    type="password"
                    value={password}
                />
            </>}

            {displayedForm === formType.CHANGE && <>
                <FormField
                    customStyles={styles.fieldTop}
                    id="tfPassword"
                    placeholder="Nouveau mot de passe"
                    prefixIcon={padlockIcon}
                    type={isRandomized ? "text" : "password"}
                    value={newPassword}
                    onChange={(event, newValue) => {
                        setIsRandomized(false);
                        setNewPassword(newValue);
                    }}
                />
                <FormField
                    customStyles={styles.fieldBottom}
                    id="tfPasswordConfirmation"
                    placeholder="Confirmation du nouveau mot de passe"
                    prefixIcon={padlockIcon}
                    type={isRandomized ? "text" : "password"}
                    value={newPasswordConfirmation}
                    onChange={(event, newValue) => {
                        setIsRandomized(false);
                        setNewPasswordConfirmation(newValue);
                    }}
                />
            </>}

            <Stack horizontal horizontalAlign="space-between" reversed styles={styles.errorAndLink}>
                <Text id={displayedForm === formType.AUTH ? "lnkResetPassword" : "lnkBack"} styles={styles.link} onClick={toggleForm}>{displayedForm === formType.AUTH ? "Nouveau mot de passe" : "Retour"}</Text>
                {displayedForm === formType.CHANGE
                    ? !isRandomized
                        ? <Text id="btnRandomize" styles={styles.link} onClick={_randomize}>Suggérer un mot de passe sécurisé</Text>
                        : <Stack horizontal>
                            <ActionButton iconProps={{ iconName: "Cancel", styles: styles.actionButtonIcon }} onClick={_onCancel} styles={styles.actionButton}>Annuler</ActionButton>
                            <ActionButton iconProps={{ iconName: "Refresh", styles: styles.actionButtonIcon }} onClick={_randomize} styles={styles.actionButton}>Changer</ActionButton>
                        </Stack>
                    : null
                }
                {displayedForm !== formType.CHANGE && _messages /* Affichage différent selon le formulaire actif */}
            </Stack>

            <PrimaryButton
                disabled={!isFormValid}
                id="btnValidate"
                onClick={onClick}
                styles={styles.button}
            >
                {buttonLabel}
            </PrimaryButton>

            {displayedForm === formType.CHANGE && _messages /* Affichage différent selon le formulaire actif */}

        </Stack>

        <CustomModal
            id="modalResetPassword"
            isOpen={isModalOpen}
            onDismiss={hideModal}
            title="Réinitialisation de votre mot de passe"
        >
            <Text>
                Un email a été envoyé à l'adresse associée au login renseigné{!!email ? ` : ${email}` : "."}<br />
                Nous vous invitons à consulter votre boîte aux lettres et à suivre la procédure de réinitialisation de mot de passe que nous vous avons communiquée.<br />
                Si vous ne recevez pas cet email nous vous suggérons de vérifier vos courriers indésirables, de vérifier la bonne saisie de votre login (respecter les majuscules et minuscules) ou de réessayer ultérieurement.
            </Text>
        </CustomModal>

    </>;

};

export default Form;